import { useState, type ReactNode } from 'react';

import {
  addBusinessDays,
  completeTask,
  convertKind,
  highlightLimitReached,
  isHighlightEligible,
  isValidLinkUrl,
  linkUrlOf,
  normalizeTagName,
  reopenTask,
  restoreBlock,
  setHighlight,
  setLinkUrl,
  setProjectStage,
  setStage,
  suggestLinkUrl,
  trashBlock,
  type ConversionTarget,
  type LocalOperation,
  type TriageDecision,
} from '@flow/domain';
import {
  applyLocalOperations,
  queries,
  type ActiveHighlightRow,
  type CaptureDestinationRow,
  type CaptureTagRow,
  type CaptureTaxonomyRow,
  type FieldValueRow,
  type HighlightTypeRow,
} from '@flow/sync';
import {
  ChoiceDialog,
  FollowUpDialog,
  HighlightLimitDialog,
  LinkDialog,
  MoveMenu,
  UndoToast,
} from '@flow/ui/components';

import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';

/**
 * Actions on a task shared by the list views and the detail panel (design D7 of f1-detail-panel):
 * complete and reopen with undo, mark TAI with its limit, move to another stage (Seguimiento
 * asks for person and date), convert into another kind (f1-type-conversion), and send to the
 * trash or bring back from it (f1-trash-search). Every action is a domain command applied in one
 * local transaction.
 */

/** Columns of the block the actions need, from the list row or from the detail panel. */
export interface TaskBlock {
  id: string;
  workspace_id: string;
  title: string;
  kind: string;
  status: string | null;
  stage_id: string | null;
  stage_changed_at: string | null;
  completed_at: string | null;
  due_on: string | null;
  start_on: string | null;
  /** Only from the detail panel: what the conversion needs (subtask, link address, proposal). */
  parent_id?: string | null;
  body?: string | null;
  props?: string | null;
}

/** Kinds that never show in the task views (f1-type-conversion D5). */
const OUT_OF_TASK_VIEWS = new Set(['project', 'note', 'page', 'link']);

export interface TaskChild {
  id: string;
  parent_id: string;
  kind: string;
  status: string | null;
  completed_at: string | null;
}

interface Toast {
  key: string;
  message: string;
  undo: LocalOperation[];
  /** Completed during this visit: forget it on undo. */
  completedId?: string;
}

export type TaskDialog =
  | { kind: 'children'; id: string; open: number }
  | { kind: 'limit'; id: string }
  | { kind: 'move'; id: string }
  | { kind: 'follow-up'; id: string; persons: { id: string; name: string }[] }
  | { kind: 'link'; id: string; mode: 'convert' | 'change'; initial: string };

export interface TaskActionsInput {
  local: LocalDatabase;
  userId: string;
  workspaceId: string;
  stages: CaptureDestinationRow[];
  period: { id: string } | undefined;
  type: HighlightTypeRow | undefined;
  active: ActiveHighlightRow[];
  highlightsEnabled: boolean;
  today: string;
  blockOf: (id: string) => TaskBlock | undefined;
  childrenOf: (id: string) => TaskChild[];
  /** The list moves its focus to the task being acted on. */
  onAct?: (id: string) => void;
  /** Completed or undone during this visit (the list keeps them in place). */
  onCompletedHere?: (id: string, completed: boolean) => void;
}

export interface TaskActions {
  toggle: (id: string) => Promise<void>;
  toggleHighlight: (id: string) => Promise<void>;
  move: (id: string, stageId: string) => void;
  openMove: (id: string) => void;
  /** «Convertir en…»: a link first asks for its address. */
  convert: (id: string, target: ConversionTarget) => void;
  /** «Cambiar» the address of a link. */
  changeLink: (id: string) => void;
  /** To the trash with its live descendants, with «Deshacer» (REC-05). */
  trash: (id: string) => Promise<void>;
  /** Back from the trash with what went with it and its trashed ancestors (INV-12). */
  restore: (id: string, deletedAt: string) => Promise<void>;
  run: (operations: LocalOperation[], toast?: { message: string; undo: LocalOperation[] }) => void;
  dialog: TaskDialog | null;
  /** A dialog or toast of the actions is open: the list keys must stay quiet. */
  busy: boolean;
  notice: string | null;
  clearNotice: () => void;
  dialogs: ReactNode;
}

export function useTaskActions(input: TaskActionsInput): TaskActions {
  const [toast, setToast] = useState<Toast | null>(null);
  const [dialog, setDialog] = useState<TaskDialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { local, userId, workspaceId, stages, period, type, active } = input;

  const highlightOf = (id: string) => active.find((h) => h.block_id === id);
  const highlighted = (id: string) => Boolean(highlightOf(id));

  const run = async (
    operations: LocalOperation[],
    next?: Omit<Toast, 'key' | 'undo'> & { undo: LocalOperation[] },
  ) => {
    if (operations.length === 0) return;
    try {
      await applyLocalOperations(local.db, operations);
      if (next) setToast({ ...next, key: `${Date.now()}` });
    } catch (cause) {
      console.error('[flow] cambio no aplicado', cause);
      setNotice(t.asap.error);
    }
  };

  const complete = async (id: string, withChildren: boolean) => {
    const block = input.blockOf(id);
    if (!block) return;
    const highlight = highlightOf(id);
    const { operations, undo } = completeTask({
      block: { id, kind: block.kind as 'task', status: 'open', completed_at: null },
      children: input.childrenOf(id).map((child) => ({
        id: child.id,
        kind: child.kind as 'task',
        status: child.status as 'open',
        completed_at: child.completed_at,
      })),
      withChildren,
      highlight: highlight ? { id: highlight.id, achieved_at: highlight.achieved_at } : null,
    });
    input.onCompletedHere?.(id, true);
    await run(operations, { message: t.asap.completed, undo, completedId: id });
  };

  const toggle = async (id: string) => {
    const block = input.blockOf(id);
    if (!block) return;
    input.onAct?.(id);
    if (block.status === 'done') {
      const highlight = highlightOf(id);
      const { operations, undo } = reopenTask({
        block: { id, kind: block.kind as 'task', status: 'done', completed_at: block.completed_at },
        highlight: highlight ? { id: highlight.id, achieved_at: highlight.achieved_at } : null,
      });
      await run(operations, { message: t.asap.reopened, undo });
      return;
    }
    const open = input.childrenOf(id).filter((child) => child.status === 'open').length;
    if (open > 0) setDialog({ kind: 'children', id, open });
    else await complete(id, false);
  };

  const highlightType = (row: HighlightTypeRow) => ({
    id: row.id,
    max_per_period: row.max_per_period,
    limit_mode: row.limit_mode,
    eligible_kind: row.eligible_kind,
    eligible_stage_category: row.eligible_stage_category,
  });

  const applyHighlight = async (id: string, mode: 'add' | 'replace', force = false) => {
    if (!type || !period) return;
    const block = input.blockOf(id);
    const stage = stages.find((s) => s.id === block?.stage_id);
    const { operations, undo } = setHighlight({
      user_id: userId,
      type: highlightType(type),
      period_id: period.id,
      block: {
        id,
        kind: (block?.kind ?? 'task') as 'task',
        stage_category: stage?.category ?? null,
      },
      current: active.map((h) => ({ id: h.id, block_id: h.block_id, rank: h.rank })),
      mode,
      force,
    });
    await run(operations, { message: t.asap.taiMarked, undo });
  };

  const toggleHighlight = async (id: string) => {
    if (!input.highlightsEnabled || !type || !period) return;
    input.onAct?.(id);
    setNotice(null);
    if (highlighted(id)) {
      const { operations, undo } = setHighlight({
        user_id: userId,
        type: highlightType(type),
        period_id: period.id,
        block: { id, kind: 'task', stage_category: null },
        current: active.map((h) => ({ id: h.id, block_id: h.block_id, rank: h.rank })),
        mode: 'remove',
      });
      await run(operations, { message: t.asap.taiRemoved, undo });
      return;
    }
    const block = input.blockOf(id);
    const stage = stages.find((s) => s.id === block?.stage_id);
    if (
      !block ||
      !isHighlightEligible(type, { kind: block.kind, stage_category: stage?.category ?? null })
    ) {
      setNotice(t.asap.taiOnlyAsap);
      return;
    }
    if (highlightLimitReached(type, active.length)) {
      setDialog({ kind: 'limit', id });
      return;
    }
    await applyHighlight(id, 'add');
  };

  const moveTo = async (id: string, destination: CaptureDestinationRow, extra?: TriageDecision) => {
    const block = input.blockOf(id);
    if (!block) return;
    if (block.kind === 'project') {
      const moved = setProjectStage({
        block: {
          id: block.id,
          kind: 'project',
          stage_id: block.stage_id,
          stage_changed_at: block.stage_changed_at,
        },
        stage: { id: destination.id, kind_scope: destination.kind_scope },
      });
      await run(moved.operations, { message: t.asap.movedTo(destination.name), undo: moved.undo });
      return;
    }
    const decision: TriageDecision = extra ?? {
      type: 'stage',
      stage: {
        id: destination.id,
        workspace_id: destination.workspace_id,
        category: destination.category,
        kind_scope: destination.kind_scope,
      },
    };
    const result = setStage({
      move: {
        user_id: userId,
        item: {
          id: block.id,
          workspace_id: block.workspace_id,
          kind: block.kind as 'task',
          stage_id: block.stage_id,
          stage_changed_at: block.stage_changed_at,
          status: block.status as 'open',
          completed_at: block.completed_at,
          deleted_at: null,
          title: block.title,
          due_on: block.due_on,
          start_on: block.start_on,
        },
      },
      decision,
      highlights: type
        ? active
            .filter((h) => h.block_id === id)
            .map((h) => ({ id: h.id, eligible_stage_category: type.eligible_stage_category }))
        : [],
    });
    const message =
      result.withdrawn > 0
        ? `${t.asap.movedTo(destination.name)}. ${t.asap.taiWithdrawn}`
        : t.asap.movedTo(destination.name);
    await run(result.operations, { message, undo: result.undo });
  };

  const chooseMove = async (id: string, stageId: string) => {
    const destination = stages.find((s) => s.id === stageId);
    if (!destination) return;
    if (destination.category === 'waiting') {
      const persons = await readPersons(local.db, workspaceId);
      setDialog({ kind: 'follow-up', id, persons });
      return;
    }
    setDialog(null);
    await moveTo(id, destination);
  };

  const applyConversion = async (id: string, target: ConversionTarget, url?: string) => {
    const block = input.blockOf(id);
    if (!block) return;
    const result = convertKind({
      block: {
        id: block.id,
        kind: block.kind as ConversionTarget,
        parent_id: block.parent_id ?? null,
        stage_id: block.stage_id,
        stage_changed_at: block.stage_changed_at,
        status: block.status as 'open' | null,
        completed_at: block.completed_at,
        props: block.props ?? null,
      },
      target,
      stages: stages.map((s) => ({
        id: s.id,
        key: s.key,
        category: s.category,
        kind_scope: s.kind_scope,
        is_capture_default: s.is_capture_default === 1,
      })),
      highlights: type
        ? active
            .filter((h) => h.block_id === id)
            .map((h) => ({
              id: h.id,
              eligible_stage_category: type.eligible_stage_category,
              eligible_kind: type.eligible_kind,
            }))
        : [],
      ...(url === undefined ? {} : { url }),
    });
    const message = [
      t.convert.done(t.convert.article[target] ?? target),
      OUT_OF_TASK_VIEWS.has(target) ? t.convert.hidden : null,
      result.withdrawn > 0 ? t.asap.taiWithdrawn : null,
    ]
      .filter(Boolean)
      .join('. ');
    await run(result.operations, { message, undo: result.undo });
  };

  const convert = (id: string, target: ConversionTarget) => {
    const block = input.blockOf(id);
    if (!block) return;
    setNotice(null);
    if (target === 'link') {
      const initial = suggestLinkUrl(block.title, block.body ?? null) ?? '';
      setDialog({ kind: 'link', id, mode: 'convert', initial });
      return;
    }
    void applyConversion(id, target);
  };

  const changeLink = (id: string) => {
    const block = input.blockOf(id);
    if (!block) return;
    setDialog({ kind: 'link', id, mode: 'change', initial: linkUrlOf(block.props) ?? '' });
  };

  const submitLink = async (id: string, mode: 'convert' | 'change', url: string) => {
    setDialog(null);
    if (mode === 'convert') {
      await applyConversion(id, 'link', url);
      return;
    }
    const block = input.blockOf(id);
    if (!block) return;
    const result = setLinkUrl({ block: { id, props: block.props ?? null }, url });
    await run(result.operations, { message: t.convert.linkChanged, undo: result.undo });
  };

  const trash = async (id: string) => {
    setNotice(null);
    try {
      const q = queries.liveDescendants(id);
      const descendants = (await local.db.getAll<{ id: string }>(q.sql, q.params)).map(
        (row) => row.id,
      );
      const result = trashBlock({ id, descendants });
      await run(result.operations, { message: t.trash.trashed, undo: result.undo });
    } catch (cause) {
      console.error('[flow] no se ha podido eliminar', cause);
      setNotice(t.asap.error);
    }
  };

  const restore = async (id: string, deletedAt: string) => {
    setNotice(null);
    try {
      const together = queries.trashedWith(id, deletedAt);
      const up = queries.trashedAncestors(id);
      const [descendants, ancestors] = await Promise.all([
        local.db.getAll<{ id: string; deleted_at: string }>(together.sql, together.params),
        local.db.getAll<{ id: string; deleted_at: string }>(up.sql, up.params),
      ]);
      const result = restoreBlock({ block: { id, deleted_at: deletedAt }, descendants, ancestors });
      await run(result.operations, { message: t.trash.restored, undo: result.undo });
    } catch (cause) {
      console.error('[flow] no se ha podido restaurar', cause);
      setNotice(t.asap.error);
    }
  };

  const dialogBlock = dialog ? input.blockOf(dialog.id) : undefined;
  // A project moves among the stages of projects, the rest among those of tasks (INV-04).
  const scope = dialogBlock?.kind === 'project' ? 'project' : 'task';
  const moveOptions = stages
    .filter(
      (s) => s.kind_scope === scope && s.category !== 'inbox' && s.id !== dialogBlock?.stage_id,
    )
    .map((s) => ({ id: s.id, label: s.name }));
  const waitingStage = stages.find((s) => s.category === 'waiting' && s.kind_scope === 'task');

  const dialogs = (
    <>
      {toast ? (
        <UndoToast
          toastKey={toast.key}
          message={toast.message}
          onUndo={() => {
            const current = toast;
            setToast(null);
            if (current.completedId) input.onCompletedHere?.(current.completedId, false);
            void run(current.undo);
          }}
          onExpire={() => setToast(null)}
          labels={{ undo: t.asap.undo }}
        />
      ) : null}
      {dialog?.kind === 'children' ? (
        <ChoiceDialog
          title={t.asap.subtasksTitle}
          message={t.asap.subtasksQuestion(dialog.open)}
          choices={[
            { id: 'with', label: t.asap.withChildren },
            { id: 'only', label: t.asap.onlyThis },
          ]}
          onChoose={(choice) => {
            const id = dialog.id;
            setDialog(null);
            void complete(id, choice === 'with');
          }}
          onCancel={() => setDialog(null)}
          labels={{ cancel: t.asap.cancel }}
        />
      ) : null}
      {dialog?.kind === 'limit' && type ? (
        <HighlightLimitDialog
          mode={type.limit_mode}
          message={t.triage.limit.message(active.length, type.max_per_period ?? 0)}
          current={active.map((h) => h.title ?? '')}
          onReplace={() => {
            const id = dialog.id;
            setDialog(null);
            void applyHighlight(id, 'replace');
          }}
          onAddAnyway={() => {
            const id = dialog.id;
            setDialog(null);
            void applyHighlight(id, 'add', true);
          }}
          onCancel={() => setDialog(null)}
          labels={t.triage.limit}
        />
      ) : null}
      {dialog?.kind === 'move' ? (
        <MoveMenu
          title={t.asap.moveTitle}
          options={moveOptions}
          onChoose={(stageId) => void chooseMove(dialog.id, stageId)}
          onCancel={() => setDialog(null)}
          labels={{ cancel: t.asap.cancel }}
        />
      ) : null}
      {dialog?.kind === 'link' ? (
        <LinkDialog
          title={dialog.mode === 'convert' ? t.convert.linkTitle : t.convert.changeLinkTitle}
          initial={dialog.initial}
          isValid={isValidLinkUrl}
          onSubmit={(url) => void submitLink(dialog.id, dialog.mode, url)}
          onCancel={() => setDialog(null)}
          labels={{
            url: t.convert.url,
            submit: dialog.mode === 'convert' ? t.convert.submit : t.convert.save,
            cancel: t.convert.cancel,
            invalid: t.convert.invalid,
          }}
        />
      ) : null}
      {dialog?.kind === 'follow-up' && waitingStage ? (
        <FollowUpDialog
          persons={dialog.persons}
          defaultDate={addBusinessDays(input.today, 3)}
          normalize={normalizeTagName}
          onCancel={() => setDialog(null)}
          onConfirm={({ person, date }) => {
            const id = dialog.id;
            setDialog(null);
            void (async () => {
              const decision = await waitingDecision(
                local.db,
                workspaceId,
                id,
                waitingStage,
                person,
                date,
              );
              if (decision) await moveTo(id, waitingStage, decision);
              else setNotice(t.asap.error);
            })();
          }}
          labels={t.triage.followUp}
        />
      ) : null}
    </>
  );

  return {
    toggle,
    toggleHighlight,
    move: (id, stageId) => void chooseMove(id, stageId),
    openMove: (id) => setDialog({ kind: 'move', id }),
    convert,
    changeLink,
    trash,
    restore,
    run: (operations, next) => void run(operations, next),
    dialog,
    busy: dialog !== null,
    notice,
    clearNotice: () => setNotice(null),
    dialogs,
  };
}

/** Persons of the workspace (taxonomy `+`), for «Esperando a». */
export async function readPersons(db: LocalDatabase['db'], workspaceId: string) {
  const taxonomiesQuery = queries.captureTaxonomies(workspaceId);
  const tagsQuery = queries.captureTags(workspaceId);
  const [taxonomies, tags] = await Promise.all([
    db.getAll<CaptureTaxonomyRow>(taxonomiesQuery.sql, taxonomiesQuery.params),
    db.getAll<CaptureTagRow>(tagsQuery.sql, tagsQuery.params),
  ]);
  const person = taxonomies.find((taxonomy) => taxonomy.prefix === '+');
  return tags
    .filter(
      (tag) => tag.taxonomy_id === person?.id && tag.parent_id === null && !tag.merged_into_id,
    )
    .map((tag) => ({ id: tag.id, name: tag.name }));
}

/** «Mover a Seguimiento» asks the same data as the triage (docs/04 §2 rule 4). */
async function waitingDecision(
  db: LocalDatabase['db'],
  workspaceId: string,
  blockId: string,
  stage: CaptureDestinationRow,
  person: { id?: string; name: string },
  date: string,
): Promise<TriageDecision | null> {
  const taxonomiesQuery = queries.captureTaxonomies(workspaceId);
  const taxonomy = (
    await db.getAll<CaptureTaxonomyRow>(taxonomiesQuery.sql, taxonomiesQuery.params)
  ).find((row) => row.prefix === '+');
  const waitingQuery = queries.fieldDefByKey(workspaceId, 'waiting_on');
  const followQuery = queries.fieldDefByKey(workspaceId, 'follow_up_on');
  const waitingOn = await db.getOptional<{ id: string }>(waitingQuery.sql, waitingQuery.params);
  const followUpOn = await db.getOptional<{ id: string }>(followQuery.sql, followQuery.params);
  if (!taxonomy || !waitingOn || !followUpOn) return null;
  const previousQuery = queries.blockFieldValues(blockId);
  const previous = await db.getAll<FieldValueRow>(previousQuery.sql, previousQuery.params);
  return {
    type: 'waiting',
    stage: {
      id: stage.id,
      workspace_id: stage.workspace_id,
      category: stage.category,
      kind_scope: stage.kind_scope,
    },
    waiting_on: {
      taxonomy: {
        id: taxonomy.id,
        workspace_id: taxonomy.workspace_id,
        cardinality: taxonomy.cardinality,
      },
      path: [person.id ? { name: person.name, existing_id: person.id } : { name: person.name }],
    },
    follow_up_on: date,
    fields: { waiting_on: waitingOn.id, follow_up_on: followUpOn.id },
    previous: previous
      .filter((row) => row.field_id === waitingOn.id || row.field_id === followUpOn.id)
      .map((row) => ({
        ...row,
        value_bool:
          row.value_bool === null ? null : row.value_bool === 1 ? (1 as const) : (0 as const),
        value_json: row.value_json === null ? null : (JSON.parse(row.value_json) as string),
      })),
  };
}
